import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { allBlogArticles } from '../../mocks/blogArticles';
import BlogCard from './components/BlogCard';
import NewsletterSignup from './components/NewsletterSignup';
import AuthorBio from './components/AuthorBio';

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => allBlogArticles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allBlogArticles
      .filter((a) => a.id !== article.id)
      .filter((a) => a.category === article.category || a.state === article.state)
      .slice(0, 3);
  }, [article]);

  const calculatorPath = article
    ? article.countrySlug === 'uk'
      ? `/${article.countrySlug}/${article.stateSlug}`
      : `/${article.countrySlug}/${article.stateSlug}/${article.citySlug}`
    : '/';

  const statePath = article && article.countrySlug === 'usa' ? `/states/${article.stateSlug}` : null;

  useDocumentMeta({
    title: article ? `${article.title} | Salary Tax Calculator Blog` : 'Article Not Found',
    description: article?.excerpt || '',
    keywords: article ? `${article.city} tax tips, ${article.city} tax guide, ${article.category}, salary tax ${article.city}` : '',
    canonical: `https://mysalarycalculator.net/blog/${slug}`,
    ogTitle: article?.title || '',
    ogDescription: article?.excerpt || '',
    ogUrl: `https://mysalarycalculator.net/blog/${slug}`,
    ogImage: article?.image || '',
    twitterCard: 'summary_large_image',
    twitterTitle: article?.title || '',
    twitterDescription: article?.excerpt || '',
    twitterImage: article?.image || '',
    structuredData: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.image,
            "author": { "@type": "Person", "name": article.author },
            "datePublished": article.date,
            "publisher": {
              "@type": "Organization",
              "name": "Salary Tax Calculator",
              "logo": {
                "@type": "ImageObject",
                "url": "https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
              }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mysalarycalculator.net/" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mysalarycalculator.net/blog" },
              { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://mysalarycalculator.net/blog/${slug}` }
            ]
          }
        ]
      : []
  });

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-6">
            <i className="ri-article-line text-4xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
          <p className="text-gray-500 mb-6">The article you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator Logo"
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">
                  <a href="/" className="hover:text-teal-600 transition-colors cursor-pointer">Salary Tax Calculator</a>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a href="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">Calculator</a>
              <a href="/blog" className="text-sm font-medium text-teal-600 cursor-pointer hidden md:block">Blog</a>
            </div>
          </div>
        </nav>
      </header>

      <main role="main">
        {/* Breadcrumbs */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <a href="/blog" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">Blog</a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium truncate max-w-[200px]">{article.city}</span>
          </nav>
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap">
                {article.category}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full whitespace-nowrap">
                {article.city}, {article.state}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
              <strong>{article.title}</strong>
            </h1>
            <p className="text-lg text-gray-600 mb-6">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full">
                  <i className="ri-user-line text-sm text-gray-600"></i>
                </div>
                <span className="font-medium text-gray-700">{article.author}</span>
              </div>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          {/* Hero Image */}
          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={`${article.title} - ${article.city} tax guide`}
              title={`${article.city} tax tips and strategies`}
              className="w-full h-full object-cover object-top"
              width="800"
              height="400"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {article.content.map((block, index) => {
              if (block.type === 'heading') {
                return (
                  <h3 key={index} className="text-xl font-bold text-gray-900 mt-10 mb-4">
                    <strong>{block.text}</strong>
                  </h3>
                );
              }
              if (block.type === 'tip') {
                return (
                  <div key={index} className="bg-teal-50 border-l-4 border-teal-500 p-5 rounded-r-xl my-8">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-white text-sm"></i>
                      </div>
                      <p className="text-teal-800 font-medium text-base leading-relaxed">{block.text}</p>
                    </div>
                  </div>
                );
              }
              return (
                <p key={index} className="text-gray-700 leading-relaxed mb-5 text-base">
                  {block.text}
                </p>
              );
            })}
          </div>

          {/* Author Bio */}
          <AuthorBio authorName={article.author} articleDate={article.date} />

          {/* CTA to Calculator */}
          <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-xl mx-auto mb-4">
              <i className="ri-calculator-line text-2xl text-teal-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Calculate Your {article.city} Take-Home Pay
            </h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              See exactly how these tax tips affect your paycheck with our free {article.city} salary calculator.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={calculatorPath}
                onClick={(e) => { e.preventDefault(); navigate(calculatorPath); }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line"></i>
                Open {article.city} Calculator
              </a>
              {statePath && (
                <a
                  href={statePath}
                  onClick={(e) => { e.preventDefault(); navigate(statePath); }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-line"></i>
                  {article.state} Tax Guide
                </a>
              )}
            </div>
          </div>

          {/* Share */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500">Share this article:</span>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`https://mysalarycalculator.net/blog/${article.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  aria-label="Share on Twitter"
                >
                  <i className="ri-twitter-x-line text-gray-600"></i>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://mysalarycalculator.net/blog/${article.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  aria-label="Share on LinkedIn"
                >
                  <i className="ri-linkedin-line text-gray-600"></i>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://mysalarycalculator.net/blog/${article.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  aria-label="Share on Facebook"
                >
                  <i className="ri-facebook-line text-gray-600"></i>
                </a>
              </div>
              <button
                onClick={() => navigate('/blog')}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-arrow-left-line"></i> All Articles
              </button>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2"><strong>Related Tax Tips &amp; Salary Guides</strong></h2>
              <p className="text-gray-500 text-sm mb-8">More tax strategies to maximize your take-home pay</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <BlogCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter */}
        <NewsletterSignup formUrl="https://readdy.ai/api/form/d69uhq7mvg9ih2c7a7c0" />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">Accurate tax calculations for locations worldwide.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4"><a href="/blog" className="hover:text-teal-400 transition-colors cursor-pointer">Popular Articles</a></h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/blog/manhattan-tax-tips-2026" className="hover:text-white transition-colors cursor-pointer">Manhattan Tax Tips</a></li>
                <li><a href="/blog/san-francisco-tech-taxes" className="hover:text-white transition-colors cursor-pointer">SF Tech Tax Guide</a></li>
                <li><a href="/blog/austin-no-state-tax-guide" className="hover:text-white transition-colors cursor-pointer">Austin Zero Tax Guide</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Calculators</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer">Manhattan, NY</a></li>
                <li><a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer">San Francisco, CA</a></li>
                <li><a href="/uk/london" className="hover:text-white transition-colors cursor-pointer">London, UK</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 Salary Tax Calculator. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogArticlePage;
